import './index.css'

import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { bindActionCreators } from 'redux'

import api from '@disco/api/server'
import { PHONE } from '@disco/constants/DeviceTypes'
import { TRACK } from '@disco/constants/ItemTypes'
import { MAIN } from '@disco/constants/Nesting'
import { toggleTrackInLibrary } from '@disco/state-management/actions/ExplorerActions'
import { addNestedTracksToMain } from '@disco/state-management/actions/NestingActions'
import {
  resetSelection,
  selectShortlistItems,
} from '@disco/state-management/actions/SelectionActions'
import { cancelShortlistUploadTask } from '@disco/state-management/actions/shortlist-upload-actions'
import {
  removeSelectedItems,
  removeTempItem,
} from '@disco/state-management/actions/ShortlistActions'
import { uploadFilesToMainTrack } from '@disco/state-management/actions/upload-actions'
import { getUserGroups } from '@disco/state-management/selectors/groups'
import { getNestingLabel } from '@disco/state-management/selectors/nesting'
import {
  getAllSelectedItems,
  getShortlistsSelection,
} from '@disco/state-management/selectors/selection'
import { getShortlistSections } from '@disco/state-management/selectors/shortlists'
import {
  getExplorerComponentHeight,
  getTrackTextSettings,
} from '@disco/state-management/selectors/user'
import { handleModifierKeysSelection } from '@disco/utils/selection'
import { getNestedTracksCount } from '@disco/utils/tracks'

import type { ParentProps } from 'types/explorer'
import type { Group } from 'types/group'
import type { PlaylistId } from 'types/playlist'
import type { ShortlistSection } from 'types/shortlist'
import type { Track } from 'types/track'

import { SelectionContext } from 'contexts/selection'

import ActionStar from 'components/common/action-star'
import TrackItem from 'components/common/track-item'
import TrackVersionsButton from 'components/nesting/track-versions-button'
import TrackActions from 'components/track-actions'
import TrackContextMenu from 'components/track-actions/track-context-menu'
import TrackTooltip from 'components/track-tooltip'
import Button from 'components/ui/button'
import Draggable from 'components/ui/draggable'
import { HoverProvider } from 'components/ui/hover-provider'
import Lozenge from 'components/ui/lozenge'
import type { PaneItemAppearance } from 'components/ui/pane-item'
import getTrackStatus from 'components/utils/track-status'

import { useDispatch, useSelector } from 'hooks/use-redux'

import ShortlistTrackVersions from '../track-versions'

const getIndexWithSections = (
  index: number,
  sections: ShortlistSection[],
): number => {
  const offset = sections.filter(({ position }) => position <= index).length
  return index + offset
}

type Props = {
  index: number
  uid?: string | null | undefined
  itemIndex: number
  showTrackVersions?: boolean
  sourcePlaylistId: PlaylistId | null | undefined
  sourceShortlistId: string
  track: Track
  onShowTrackVersions?: (open: boolean) => void
}

const ShortlistTrack = ({
  // TODO: refactor ShortlistTrack to pass only 1 prop (item) instead of 4 props (index+uid+itemIndex+track)
  index,
  uid,
  itemIndex,
  showTrackVersions = false,
  sourcePlaylistId,
  sourceShortlistId,
  track,
  onShowTrackVersions,
}: Props) => {
  const dispatch = useDispatch()
  const isPhone: boolean = useSelector(
    state => state.app.mobileDeviceType === PHONE,
  )
  const sections: ShortlistSection[] = useSelector(state =>
    getShortlistSections(state, {
      shortlistId: sourceShortlistId,
    }),
  )
  const selectionContext = useContext(SelectionContext)
  const shortlistsSelection: Record<string, any> = useSelector(
    getShortlistsSelection,
  )
  const selection: Record<string, any> =
    shortlistsSelection[selectionContext.id ?? '']
  const selectedItems = useSelector(getAllSelectedItems)
  const textSettings = useSelector(getTrackTextSettings)
  const isPartOfMultipleSelection: boolean =
    selection?.indexes.length > 1 && selection?.indexes.includes(index)
  const userGroups: Group[] = useSelector(getUserGroups)
  const componentHeight: 'Default' | 'Compact' = useSelector(
    getExplorerComponentHeight,
  )
  const isStarred: boolean = useMemo(() => {
    const starredGroup = userGroups?.[1]
    if (!starredGroup) return false
    return track.groups.includes(starredGroup.url)
  }, [track, userGroups])
  const boundSelectShortlistItems = useCallback(
    (selectionId, selectionRange, selectionType) =>
      dispatch(
        selectShortlistItems(
          selectionContext.id,
          selectionRange,
          selectionType,
        ),
      ),
    [dispatch, selectionContext.id],
  )
  const handleTrackClick = useCallback(
    (event: MouseEvent) => {
      // handle selection actions
      if (event.metaKey || event.ctrlKey || event.shiftKey) {
        handleModifierKeysSelection(event, {
          selection: shortlistsSelection,
          index,
          selectionId: selectionContext.id,
          selectItems: boundSelectShortlistItems,
        })
        return
      }

      // handle regular clicks, regular click reset selection
      dispatch(resetSelection())

      if (!isPartOfMultipleSelection) {
        handleModifierKeysSelection(event, {
          selection: shortlistsSelection,
          index,
          selectionId: selectionContext.id,
          selectItems: boundSelectShortlistItems,
          indivSelectAllowed: true,
        })
      }
    },
    [
      boundSelectShortlistItems,
      dispatch,
      index,
      isPartOfMultipleSelection,
      selectionContext.id,
      shortlistsSelection,
    ],
  )
  const handleFavoriteClick = useCallback(() => {
    dispatch(toggleTrackInLibrary(track))
  }, [dispatch, track])
  const handleDelete = useCallback(
    (event: React.SyntheticEvent<any>) => {
      event.stopPropagation()

      if (track.tempId) {
        if (track.externalUploadId) {
          api.cancelProviderFileUpload(track.externalUploadId)
          dispatch(removeTempItem(sourceShortlistId, track))
        } else {
          dispatch(cancelShortlistUploadTask(sourceShortlistId, track))
        }

        return
      }

      dispatch(removeSelectedItems(sourceShortlistId, [index]))
    },
    [dispatch, index, sourceShortlistId, track],
  )
  const handleDragStart = useCallback(() => {
    dispatch(resetSelection())
    // collapse nested tracks view to reduce height of the dragged element
    setOpen(false)
  }, [dispatch])
  const [open, setOpen] = useState(showTrackVersions)
  useEffect(() => {
    setOpen(showTrackVersions)
  }, [showTrackVersions])
  const versionsCount = getNestedTracksCount(track)
  const { isTemp } = getTrackStatus(track)
  let appearance: PaneItemAppearance = 'default'

  if (selection?.indexes.includes(index)) {
    if (selection?.indexes.length > 1) {
      appearance = 'selected'
    } else if (selection?.indexes.length === 1) {
      appearance = 'active'
    }
  }

  let customEditUrl: string | null | undefined = null

  if (typeof index === 'number' && sourceShortlistId) {
    customEditUrl = `/shortlists/${sourceShortlistId}/tracks/${itemIndex}/edit-metadata`
  }

  const playbackContext = {
    sourcePlaylistId,
    sourceShortlistId,
  }
  // Shared by the three-dot button and the row's right-click menu, so the two
  // always offer the same actions.
  const trackMenuProps = {
    addToCustomGroup: true,
    addToNewCustomGroup: true,
    addToShortlist: isPhone,
    allowSharing: true,
    copyMetadata: true,
    customEditMetadataUrl: customEditUrl,
    deleteTrack: true,
    downloadTrack: true,
    editMetadata: true,
    itemIndex: index,
    manageNesting: true,
    manageTrackFormats: true,
    mixpanelContext: 'Shortlist' as const,
    permissionsAndSharing: true,
    removeFromShortlist: true,
    sourcePlaylistId,
    sourceShortlistId,
    track,
  }
  const nestingLabel = getNestingLabel(track)
  const parentProps: ParentProps = {
    // TODO: fix tracks selection
    loadNext: () => {},
    appendTracksToPlaylist: () => {},
    componentHeight: componentHeight,
    groups: [],
    isLoading: false,
    paneSelection: [],
    pane: { ...track },
    selectedItems,
    library: null,
    ...bindActionCreators(
      {
        uploadFilesToMainTrack,
        addNestedTracksToMain,
      },
      dispatch,
    ),
  }
  const isMainTrack = nestingLabel && nestingLabel === MAIN
  const versionsButton = isMainTrack && versionsCount > 0 && (
    <TrackVersionsButton
      open={open}
      count={versionsCount}
      onClick={() => {
        setOpen(!open)

        if (onShowTrackVersions) {
          onShowTrackVersions(!open)
        }
      }}
    />
  )
  let nodeRight

  if (nestingLabel) {
    nodeRight =
      nestingLabel === MAIN ? (
        <div className="ShortlistVersionsIndicator">{versionsButton}</div>
      ) : (
        <Lozenge color="gray" text={nestingLabel} bold />
      )
  }

  return (
    <div className="ShortlistTrack" data-testid="shortlist-item">
      <Draggable
        id={`${sourceShortlistId}-track-${
          uid || track.uploadId || track.tempId || track.id
        }`}
        data={{
          isPartOfMultipleSelection,
          selection: selection?.indexes.map(index =>
            getIndexWithSections(index, sections),
          ),
          track,
        }}
        type={TRACK}
        onDragStart={isPartOfMultipleSelection ? undefined : handleDragStart}
      >
        <HoverProvider enableTouchSupport>
          <TrackContextMenu
            {...trackMenuProps}
            disabled={track.outside_your_territories}
          >
            <TrackItem
              appearance={appearance}
              track={track}
              textSettings={textSettings} // TODO: move `location` to context
              location="shortlist"
              itemIndex={itemIndex}
              showIndex
              actions={
                <>
                  {!isPhone && (
                    <>
                      {!track.outside_your_territories && (
                        <TrackTooltip
                          customEditMetadataUrl={customEditUrl}
                          showViewLink
                          track={track}
                        />
                      )}

                      {!isTemp && !track.outside_your_territories && (
                        <ActionStar
                          active={isStarred}
                          onClick={handleFavoriteClick}
                        />
                      )}
                      {/*
              Disable removal action for dropbox uploads till we receive
              external upload id to be able properly cancel them
              */}

                      {(!track.file?.link ||
                        (track.externalUploadId && track.file?.link)) && (
                        <Button
                          appearance="icon"
                          disabled={track.importing}
                          compact
                          icon="clear"
                          tooltip="Remove from shortlist"
                          onClick={handleDelete}
                        />
                      )}
                    </>
                  )}
                  <TrackActions
                    {...trackMenuProps}
                    buttonSize="compact"
                    versionsButton={versionsButton}
                    hideEls={
                      track.outside_your_territories
                        ? [
                            'share_shortcut',
                            'native_tags',
                            'context_menu',
                            'versions_button',
                            'notes_shortcut',
                          ]
                        : ['notes_shortcut']
                    }
                  />
                </>
              }
              nodeRight={nodeRight}
              onClick={handleTrackClick}
              {...playbackContext}
            />
          </TrackContextMenu>
        </HoverProvider>
      </Draggable>
      {isMainTrack && open && (
        <ShortlistTrackVersions parentProps={parentProps} />
      )}
    </div>
  )
}

export default React.memo<Props>(ShortlistTrack)
